import type { SystemStyleObject } from "@chakra-ui/react"
import { ButtonProps } from "@chakra-ui/react"
import RichTextEditor from "@pathwright/ux/src/components/texteditor/RichTextEditorLazy"
import type {
  RichTextEditorControlBarProps,
  RichTextEditorEditorOptions,
  RichTextEditorPickedMedia,
  RichTextEditorUploadRenderProps
} from "@pathwright/ux/src/components/texteditor/types"
import { useMemo } from "react"
import MediaManager from "../media-manager/MediaManager"

export type RichTextEditorWithMediaManagerProps = {
  value: string
  onChange: (value: string) => void
  mediaManagerContextKey: string
  mode?: "view" | "edit"
  controlBar?: RichTextEditorControlBarProps
  editorStyles?: SystemStyleObject
  /** Enables "/" slash commands for image, attachment, and embed. @default false */
  enableSlashMenu?: boolean
  autoFocus?: boolean
  disabled?: boolean
  placeholder?: string
}

const RichTextEditorWithMediaManager = ({
  value,
  onChange,
  mediaManagerContextKey,
  mode = "view",
  controlBar,
  editorStyles,
  enableSlashMenu = false,
  autoFocus = false,
  disabled = false,
  placeholder
}: RichTextEditorWithMediaManagerProps) => {
  const editorOptions = useMemo((): RichTextEditorEditorOptions | undefined => {
    if (!placeholder) return undefined
    return { placeholder }
  }, [placeholder])

  const renderImageUpload = ({
    renderTrigger,
    insert
  }: RichTextEditorUploadRenderProps) => (
    <MediaManager
      contextKey={`${mediaManagerContextKey}-image`}
      mediaType="image"
      pickOptions={{ maxFiles: 1 }}
      renderLoading={() => null}
      onChooseMedia={(media: RichTextEditorPickedMedia) => {
        insert(media)
      }}
      renderButton={({ onClick }: ButtonProps) => {
        return renderTrigger((event) => {
          onClick?.(event)
        })
      }}
    />
  )

  const renderFileUpload = ({
    renderTrigger,
    insert
  }: RichTextEditorUploadRenderProps) => (
    <MediaManager
      contextKey={`${mediaManagerContextKey}-attachment`}
      mediaType="any"
      pickOptions={{ maxFiles: 1 }}
      renderLoading={() => null}
      onChooseMedia={(media: RichTextEditorPickedMedia) => {
        insert(media)
      }}
      renderButton={({ onClick }: ButtonProps) => {
        return renderTrigger((event) => {
          onClick?.(event)
        })
      }}
    />
  )

  return (
    <RichTextEditor
      value={value}
      onChange={onChange}
      mode={mode}
      permissionMode="editable"
      controlBar={controlBar}
      editorStyles={editorStyles}
      enableSlashMenu={enableSlashMenu}
      renderUploadImage={enableSlashMenu ? renderImageUpload : undefined}
      renderUploadFile={enableSlashMenu ? renderFileUpload : undefined}
      autoFocus={autoFocus}
      disabled={disabled}
      editor={editorOptions}
    />
  )
}

export default RichTextEditorWithMediaManager
